import React, { useEffect, useState, createElement } from 'react';
import { Download } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    city?: string;
    state?: string;
    eventVenueCityState?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  assignedTechnician?: string;
  assignedDistributor?: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays: number;
  };
}
export function ExternalScheduleView() {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadRentals();
  }, []);
  const loadRentals = async () => {
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'Gen_3_Scoring_Rental_Report'
      };
      const result = await callWFAPI(0, 'zohoCreator', data);
      const transformedRentals: Rental[] = result.map((rental: any) => ({
        id: rental.ID,
        name: rental.Name,
        email: rental.Email,
        phone: rental.Phone_Number,
        eventName: rental.Event_Name || 'Unnamed Event',
        customerInfo: {
          city: rental.Shipping_Address?.district_city,
          state: rental.Shipping_Address?.state_province,
          eventVenueCityState: rental.Event_Venue_City_State
        },
        proposedEventDate: rental.Proposed_Event_Date,
        alternateDate: rental.Alternate_Date,
        status: rental.Status || 'Pending',
        total: parseFloat(rental.Total_Amount) || 0,
        assignedTechnician: rental.Technician?.display_value,
        assignedDistributor: rental.Distributor?.display_value,
        selections: {
          gen3Body: parseInt(rental.Gen3_Body_Quantity) || 0,
          gen3Head: parseInt(rental.Gen3_Head_Quantity) || 0,
          gen3JudgesOnly: parseInt(rental.Gen3_Judges_Only_Quantity) || 0,
          fullGen3Ring: parseInt(rental.Full_Gen3_Ring_Quantity) || 0,
          requireTech: rental.Requires_Technician === 'true',
          numberOfDays: parseInt(rental.Number_Of_Days) || 1
        }
      }));
      setRentals(transformedRentals);
    } catch (error) {
      console.error('Error loading rentals:', error);
    } finally {
      setLoading(false);
    }
  };
  const downloadCSV = () => {
    const headers = ['Event Date', 'End Date', 'Days', 'Event Name', 'Customer Name', 'Email', 'Phone', 'City', 'State', 'Venue City/State', 'Status', 'Full Rings', 'Gen3 Body', 'Gen3 Head', 'Judges Only', 'Tech Required', 'Assigned Technician'];
    const escapeCSV = (value: string) => {
      if (value == null) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const rows = sortedRentals.map(rental => {
      const startDate = rental.proposedEventDate ? new Date(rental.proposedEventDate + 'T00:00:00').toLocaleDateString('en-US') : '';
      let endDate = '';
      if (rental.proposedEventDate && rental.selections.numberOfDays > 1) {
        const end = new Date(rental.proposedEventDate + 'T00:00:00');
        end.setDate(end.getDate() + rental.selections.numberOfDays - 1);
        endDate = end.toLocaleDateString('en-US');
      }
      return [startDate, endDate, rental.selections.numberOfDays, rental.eventName, rental.name, rental.email, rental.phone, rental.customerInfo?.city || '', rental.customerInfo?.state || '', rental.customerInfo?.eventVenueCityState || '', rental.status, rental.selections.fullGen3Ring, rental.selections.gen3Body, rental.selections.gen3Head, rental.selections.gen3JudgesOnly, rental.selections.requireTech ? 'Yes' : 'No', rental.assignedTechnician || ''].map(v => escapeCSV(String(v)));
    });
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `events_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sortedRentals = rentals.filter(r => {
    if (!r.proposedEventDate) return false;
    const eventDate = new Date(r.proposedEventDate + 'T00:00:00');
    return eventDate >= today;
  }).sort((a, b) => {
    return new Date(a.proposedEventDate).getTime() - new Date(b.proposedEventDate).getTime();
  });
  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-757">
        <div className="text-center" data-id="element-758">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" data-id="element-759"></div>
          <p className="text-slate-600" data-id="element-760">Loading schedule...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white p-8" data-id="element-761">
      {/* Header with Export */}
      <div className="max-w-7xl mx-auto mb-8" data-id="element-762">
        <div className="flex items-center justify-between mb-6" data-id="element-763">
          <h1 className="text-3xl font-bold text-slate-900" data-id="element-764">Event Schedule</h1>
          <button onClick={downloadCSV} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium" data-id="element-765">
            <Download size={20} data-id="element-766" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Simple Table */}
      <div className="max-w-7xl mx-auto" data-id="element-767">
        <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-768">
          <table className="w-full" data-id="element-769">
            <thead className="bg-slate-100 border-b border-slate-200" data-id="element-770">
              <tr data-id="element-771">
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-772">
                  Date
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-773">
                  Event Name
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-774">
                  Customer
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-775">
                  Email
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-776">
                  Location
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-777">
                  Rings
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-778">
                  Technician
                </th>
                <th className="text-left p-4 text-sm font-semibold text-slate-700" data-id="element-779">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-780">
              {sortedRentals.map(rental => <tr key={rental.id} className="hover:bg-slate-50" data-id="element-781">
                  <td className="p-4 text-sm text-slate-900" data-id="element-782">
                    {(() => {
                  const startDate = new Date(rental.proposedEventDate + 'T00:00:00');
                  const startDateStr = startDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  if (rental.selections.numberOfDays > 1) {
                    const endDate = new Date(startDate);
                    endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                    const endDateStr = endDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    });
                    return <div data-id="element-783">
                            <div className="font-medium" data-id="element-784">
                              {startDateStr} - {endDateStr}
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-785">
                              {rental.selections.numberOfDays} days
                            </div>
                          </div>;
                  }
                  return startDateStr;
                })()}
                  </td>
                  <td className="p-4 text-sm text-slate-900 font-medium" data-id="element-786">
                    {rental.eventName}
                  </td>
                  <td className="p-4 text-sm text-slate-700" data-id="element-787">{rental.name}</td>
                  <td className="p-4 text-sm text-slate-700" data-id="element-788">
                    {rental.email || '-'}
                  </td>
                  <td className="p-4 text-sm text-slate-700" data-id="element-789">
                    {rental.customerInfo?.eventVenueCityState || [rental.customerInfo?.city, rental.customerInfo?.state].filter(Boolean).join(', ') || '-'}
                  </td>
                  <td className="p-4 text-sm text-slate-900 font-semibold" data-id="element-790">
                    {rental.selections.fullGen3Ring}
                  </td>
                  <td className="p-4 text-sm text-slate-700" data-id="element-791">
                    {rental.assignedTechnician || '-'}
                  </td>
                  <td className="p-4" data-id="element-792">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${rental.status === 'Paid in Full' ? 'bg-green-100 text-green-800' : rental.status === 'Deposit' ? 'bg-yellow-100 text-yellow-800' : rental.status === 'Confirmed' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-800'}`} data-id="element-793">
                      {rental.status}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>

          {sortedRentals.length === 0 && <div className="text-center py-12" data-id="element-794">
              <p className="text-slate-600" data-id="element-795">No events scheduled</p>
            </div>}
        </div>
      </div>
    </div>;
}